import { useFormApi } from '@data-driven-forms/react-form-renderer';
import { useEffect, useMemo, useState } from 'react';

import Box from '~/common/components/Box';
import GlobalLayout from '~/common/components/GlobalLayout';
import PrintInfoHeader from '~/common/components/PrintInfoHeader';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import { RULE_ID } from '~/common/constants';
import { RENTAL_INCOME } from '~/common/constants/api-ids.js';
import useErrorScroll from '~/common/hooks/useErrorScroll';
import useFullPageWithPDFViewer from '~/common/hooks/useFullPageWithPDFViewer';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import useUrl from '~/common/hooks/useUrl';
import print from '~/common/util/print';
import { AuditHistoryTypes } from '~/pages/AuditHistory/constants';
import IncomeHeaderWithCalculationHistory from '~/pages/IncomeCalculator/Header/IncomeHeaderWithCalculationHistory.js';
import { ButtonLabels } from '~/pages/IncomeCalculator/constants';
import { IncomeRuleIds } from '~/pages/IncomeCalculator/enums';
import {
  isEmploymentIncome,
  useIncomeInformation,
} from '~/pages/IncomeCalculator/hooks';
import { TITLE_WALK_ME_PRIMARY_BUTTON_ID } from '~/pages/TitlePackageReview/utils/constants';
import titleLang from '~/pages/TitlePackageReview/utils/language.json';
import { useSelectedAlert } from '~/stores/navigationStore';

type FormTemplateProps = {
  schema?: any;
  formFields?: any;
  shouldWaitForSignalR?: boolean;
  isLoading?: boolean;
  /** Disables the primary button if this value is true and there are form validation errors */
  isPrimaryButtonDisabled?: boolean;
  /** Disable the primary button if this value is true regardless of form validation errors */
  isPrimaryButtonForceDisabled?: boolean;
  error?: string;
  checkAllFieldsFalsy?: boolean;
  toastMessage?: {
    description: string;
  };
  isPrintButtonVisible?: boolean;
  printBoxId?: string;
  throughDates?: any;
  shouldAutoCheck?: boolean;
  showCalculationHistory?: boolean;
  primaryButtonId?: string;
  /* Get rid of default header and all margins in content box */
  useBareContent?: boolean;
  customHeader?: React.ReactNode;
};

export default function FormTemplate({
  schema,
  formFields,
  shouldWaitForSignalR,
  isLoading,
  error,
  isPrimaryButtonDisabled,
  isPrimaryButtonForceDisabled,
  checkAllFieldsFalsy,
  toastMessage,
  isPrintButtonVisible = false,
  printBoxId,
  throughDates,
  shouldAutoCheck = false,
  showCalculationHistory = false,
  primaryButtonId,
  useBareContent = false,
  customHeader = null,
}: Readonly<FormTemplateProps>) {
  const [primaryButtonIdName, setPrimaryButtonIdName] = useState(
    primaryButtonId || '',
  );
  const { getState, handleSubmit } = useFormApi();
  const { submitting, valid, values, hasValidationErrors } = getState();
  const scrollToFirstErrorMessage = useErrorScroll(hasValidationErrors);
  const { data: selectedIncome } = useIncomeInformation();
  const { data: loanSummary } = useGetLoanSummary();
  const { underwritingMethod, loanNumber } = loanSummary?.loanInfo || {};

  const { ruleCategoryName, displayTitle, ruleId, ruleSubCategoryId } =
    useSelectedAlert();
  const { obfuscatedLoanIdentifier } = useUrl();

  //TODO: remove once GlobalLayout has been refactored and SDUI has been implemented for selected page
  const {
    isTitleCommitmentRuleId,
    isTitlePackageReviewRuleId,
    isFullPageWithPDFViewer,
  } = useFullPageWithPDFViewer();

  const showSnackbar = useSnackbar();
  const [isPrintMode, setIsPrintMode] = useState(false);
  const [throughDate, setThroughDate] = useState(null);
  const printPage = print(`#${printBoxId}`, schema?.title ?? displayTitle);
  const [auditHistoryParams, setAuditHistoryParams] = useState(null);
  const isNewCreditInquiry =
    ruleId === RULE_ID.NEW_DEBT_ON_CREDIT_INQUIRY_LETTER_RULE;

  useEffect(() => {
    if (Object.hasOwn(values, 'calculationId')) {
      setThroughDate(
        throughDates?.find((td) => td.calculationId == values.calculationId)
          ?.throughDate,
      );
    }
  }, [throughDates, values, values?.calculationId]);

  useEffect(() => {
    if (isPrintMode) {
      printPage();
      setIsPrintMode(false);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [isPrintMode]);

  useEffect(() => {
    if (isNewCreditInquiry) {
      setAuditHistoryParams({
        auditHistoryTypes: [AuditHistoryTypes.EsignCreditInquiry],
        obfuscatedLoanIdentifier: obfuscatedLoanIdentifier,
        ruleReferenceIdentifiers: [],
        questionCategory: 'CreditInquiry',
      });
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  //INFO: needed to remove WalkMe survey when pages for Title have validation errors
  //TODO: remove when Title WalkMe survey is no longer needed
  useEffect(() => {
    if (isTitleCommitmentRuleId) {
      hasValidationErrors
        ? setPrimaryButtonIdName('')
        : setPrimaryButtonIdName(TITLE_WALK_ME_PRIMARY_BUTTON_ID);
    }
  }, [hasValidationErrors, isTitleCommitmentRuleId]);

  const auditHistoryDataObj = {
    selectedIds: ['CreditInquiry'],
    questionIds: Array.from({ length: 9999 }, (_, i) => i),
    showButton: true,
    auditHistoryParams,
  };

  const isIncome = ruleCategoryName?.toLowerCase().includes('income') || false;
  const title = schema?.title ?? displayTitle;
  const isAssetType = ruleId === RULE_ID.ASSET_TYPE_RULE_ID;
  const isNoDocProvided =
    ruleId === IncomeRuleIds.NoDocumentationProvidedIncomeSummary;
  const isRentalIncome = ruleSubCategoryId === RENTAL_INCOME;
  const headerToDisplay = setHeaderToDisplay({
    customHeader,
    useBareContent,
    income: {
      isIncome,
      isNoDocProvided,
      isRentalIncome,
      showCalculationHistory,
    },
  });

  const globalLayoutProps = {
    heading: headerToDisplay || isAssetType ? null : title,
    customHeader: headerToDisplay,
    auditHistoryData: isNewCreditInquiry ? auditHistoryDataObj : undefined,
    forceFullPage: isFullPageWithPDFViewer,
  };

  const areAllFieldValuesFalsy = useMemo(() => {
    return Object.values(values).every((value) => !value);
  }, [values]);

  const isPrimaryBtnDisableAllFieldsFalsy =
    areAllFieldValuesFalsy && checkAllFieldsFalsy;

  return (
    <GlobalLayout
      {...globalLayoutProps}
      waitForSignalR={shouldWaitForSignalR}
      isLoading={isLoading}
      shouldAutoCheck={shouldAutoCheck}
      error={error}
      primaryButton={{
        id: primaryButtonIdName,
        isSubmitting: submitting,
        isDisabled:
          submitting ||
          (hasValidationErrors && isPrimaryButtonDisabled) ||
          isPrimaryButtonForceDisabled ||
          isPrimaryBtnDisableAllFieldsFalsy,
        onSubmit: async () => {
          if (isTitlePackageReviewRuleId && hasValidationErrors) {
            showSnackbar({
              message: titleLang.TITLE_VALIDATION_ERROR_HEADER,
              severity: 'error',
              duration: 5000,
            });
          }

          scrollToFirstErrorMessage();

          let res;
          try {
            // @ts-expect-error TS2554
            res = await handleSubmit(values);
            if (res === undefined) {
              // eslint-disable-next-line no-console
              console.error(
                'Error during form submission: Response is undefined',
                error,
              );
            }
            // Handle the response if needed
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error during form submission:', error);
          }

          if (!res && valid && toastMessage) {
            showSnackbar({
              severity: 'error',
              message: toastMessage.description,
              duration: 5000,
            });
          }

          return !!res;
        },
      }}
      secondaryButton={
        isPrintButtonVisible && {
          name: ButtonLabels.print,
          onSubmit: () => {
            setIsPrintMode(true);
          },
        }
      }
    >
      <Box
        id={printBoxId}
        sx={{
          overflowY: 'visible',
          padding: !isFullPageWithPDFViewer && !useBareContent ? '1rem' : '0',
        }}
      >
        {isEmploymentIncome(selectedIncome) && !useBareContent && (
          <PrintInfoHeader
            rows={2}
            columns={3}
            loanNumber={loanNumber}
            borrowerName={selectedIncome.borrowerName.fullName}
            ausMethod={underwritingMethod}
            employerName={selectedIncome.employerName}
            startDate={selectedIncome.startDate}
            throughDate={throughDate}
          />
        )}
        {formFields}
      </Box>
    </GlobalLayout>
  );
}

function setHeaderToDisplay({ customHeader, useBareContent, income }) {
  // TODO: refactor && write tests
  if (useBareContent) {
    return null;
  }
  if (customHeader) {
    return customHeader;
  }
  if (income.isIncome) {
    return (
      <IncomeHeaderWithCalculationHistory
        showCalculationHistory={income.showCalculationHistory}
      />
    );
  }
}
